<script setup>
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import {isEmpty} from "@/utils";
import CountryDropdown from "@/components/CountryDropdown.vue";
import BirthdayPicker from "@/components/BirthdayPicker.vue";
import {useGuestsStore} from "@/stores/guests";
import {useI18n} from "vue-i18n";
import {useToast} from "vue-toastification";
import AppIcon from "@/components/AppIcon.vue";
import {GermanCountryCode} from "@/constants";

const props = defineProps(['id', 'index'])
const emits = defineEmits(['saved'])

const guestsStore = useGuestsStore()
const i18n = useI18n()
const toast = useToast()

const isSaving = ref(false)
const form = ref(null)
let buffer = {};
const formData = computed({
  get: () => guestsStore.guests[props.index],
  set: value => buffer = value,
})

const isRegistered = computed({
  get: () => !!guestsStore.guests[props.index].hash,
  set: v => guestsStore.guests[props.index].hash = v,
})

// Die Staatsangehörigkeit ist die einzige Frage, die über den Umfang des
// Formulars entscheidet. „Ausländische Person" ist nach § 2 Abs. 1 AufenthG,
// wer nicht Deutscher im Sinne des Art. 116 Abs. 1 GG ist — das richtet sich
// nach der Staatsangehörigkeit, nicht nach dem Wohnsitz. Deutsche Gäste stehen
// gar nicht mehr auf dem Meldeschein, deshalb fragen wir sie auch nicht nach
// einer Anschrift. Solange keine Staatsangehörigkeit gewählt ist, bleibt das
// Formular kurz.
const isForeignNational = computed(() => {
  const nationality = formData.value.nationality

  return !!nationality && nationality !== GermanCountryCode
})

const isMainGuest = computed(() => props.index === 0)

// Geburtsdatum und Anschrift stehen beim Hauptgast ohnehin an — er ist der
// Vertragspartner. Mitreisende mit deutscher Staatsangehörigkeit stehen auf
// keinem Meldeschein, von ihnen genügt der Name.
const needsFullRecord = computed(() => isMainGuest.value || isForeignNational.value)

// Was das Formular nicht mehr abfragt, darf auch nicht mitgesendet werden —
// save() postet den ganzen Datensatz.
watch(isForeignNational, foreign => {
  const guest = formData.value

  if (foreign) {
    return
  }

  guest.documentType = ''
  guest.documentNumber = ''
  guest.purposeOfStay = ''

  if (needsFullRecord.value) {
    // Hauptgast: Anschrift bleibt, das Land ist dann Deutschland.
    guest.country = GermanCountryCode

    return
  }

  guest.birthday = ''
  guest.address = ''
  guest.postalCode = ''
  guest.city = ''
  guest.country = ''
})

onMounted(() => {
  if (needsFullRecord.value && !isForeignNational.value && !formData.value.country) {
    formData.value.country = GermanCountryCode
  }
})

onMounted(() => {
  buffer = guestsStore.guests[props.index]
})

// Zwischenstand: verlässt der Gast ein Feld, wandert der aktuelle Stand still
// zum Server. Ohne das geht alles verloren, sobald das Handy zuklappt — und
// gerade Mitreisende füllen ihren Teil oft zu einem anderen Zeitpunkt aus.
const DRAFT_DELAY = 700
const hasDraft = computed(() => !!formData.value.draft && !isRegistered.value)
let draftTimer = null

function scheduleDraft() {
  if (isRegistered.value) {
    return
  }

  clearTimeout(draftTimer)
  draftTimer = setTimeout(persistDraft, DRAFT_DELAY)
}

function persistDraft() {
  const guest = formData.value

  // Ein völlig leeres Formular ist kein Zwischenstand — sonst legt jedes
  // Aufklappen einen Datensatz an.
  const touched = guest.firstName || guest.lastName || guest.birthday || guest.nationality
  if (isRegistered.value || isSaving.value || !touched) {
    return
  }

  guestsStore.saveDraft(props.index).catch(err => console.error(err))
}

onUnmounted(() => clearTimeout(draftTimer))

function save() {
  // Der endgültige Speichervorgang gewinnt gegen einen noch wartenden Entwurf.
  clearTimeout(draftTimer)

  if (!form.value.checkValidity()) {
    // Der Gast wollte speichern. Auch wenn noch Pflichtfelder fehlen, bleibt
    // erhalten, was schon dasteht — sonst führt der Klick zu nichts als einer
    // Fehlermeldung.
    persistDraft()
    form.value.reportValidity()

    return;
  }

  isSaving.value = true

  guestsStore
      .saveGuest(buffer, props.index)
      .then(() => emits('saved'))
      .catch(err => {
        console.error(err)
        toast.error(i18n.t('alerts.somethingWentWrong'))
      })
      .finally(() => isSaving.value = false)

}

function validate(value, length = 2) {
  return value.length >= length;
}
</script>

<template>
  <!-- focusout statt blur: blur steigt nicht auf, würde also je Feld einen
       eigenen Listener brauchen. -->
  <form action="#" @submit.prevent="save" @focusout="scheduleDraft" ref="form" class="guest-form">
    <div class="ww-grid-2">
      <label class="ww-field" :for="props.id + '-firstname'">
        <span class="ww-field__label">{{ $t("firstName") }}</span>
        <input
            type="text"
            class="ww-input"
            :id="props.id + '-firstname'"
            :placeholder="$t('firstName')"
            :aria-invalid="isEmpty(formData.firstName) ? undefined : !validate(formData.firstName)"
            v-model="formData.firstName"

            required
            :disabled="isRegistered"
        />
      </label>

      <label class="ww-field" :for="props.id + '-lastname'">
        <span class="ww-field__label">{{ $t("lastName") }}</span>
        <input
            type="text"
            class="ww-input"
            :id="props.id + '-lastname'"
            :placeholder="$t('lastName')"
            :aria-invalid="isEmpty(formData.lastName) ? undefined : !validate(formData.lastName)"
            v-model="formData.lastName"

            required
            :disabled="isRegistered"
        />
      </label>
    </div>

    <!-- Die Staatsangehörigkeit steht immer da: sie entscheidet, was der
         Meldeschein verlangt, und damit über den Rest des Formulars. -->
    <div :class="needsFullRecord ? 'ww-grid-2' : ''">
      <birthday-picker
          v-if="needsFullRecord"
          :id="props.id + '-birthday'"
          v-model="formData.birthday"
          :required="true"
          :disabled="isRegistered"
      />

      <country-dropdown
          title="nationality"
          placeholder="select"
          :id="props.id + '-nationality'"
          v-model="formData.nationality"
          nationality

          :required="true"
          :disabled="isRegistered"
      />
    </div>

    <template v-if="needsFullRecord">
      <label class="ww-field" :for="props.id + '-address'">
        <span class="ww-field__label">{{ $t("address") }}</span>
        <input
            type="text"
            class="ww-input"
            :id="props.id + '-address'"
            :placeholder="$t('address')"
            :aria-invalid="isEmpty(formData.address) ? undefined : !validate(formData.address)"
            v-model="formData.address"

            required
            :disabled="isRegistered"
        />
      </label>

      <div class="guest-form__zip-city">
        <label class="ww-field" :for="props.id + '-postal-code'">
          <span class="ww-field__label">{{ $t("postalCode") }}</span>
          <input
              type="text"
              class="ww-input"
              :id="props.id + '-postal-code'"
              :placeholder="$t('postalCode')"
              :aria-invalid="isEmpty(formData.postalCode) ? undefined : !validate(formData.postalCode)"
              v-model="formData.postalCode"

              required
              :disabled="isRegistered"
          />
        </label>

        <label class="ww-field" :for="props.id + '-city'">
          <span class="ww-field__label">{{ $t("city") }}</span>
          <input
              type="text"
              class="ww-input"
              :id="props.id + '-city'"
              :placeholder="$t('city')"
              :aria-invalid="isEmpty(formData.city) ? undefined : !validate(formData.city)"
              v-model="formData.city"

              required
              :disabled="isRegistered"
          >
        </label>
      </div>

      <!-- Das Land der Anschrift fragen wir nur bei ausländischer
           Staatsangehörigkeit: dort gehört es auf den Meldeschein. Beim
           deutschen Hauptgast ist es Deutschland und steht schon fest. -->
      <country-dropdown
          v-if="isForeignNational"
          title="country"
          placeholder="select"
          :id="props.id + '-country'"
          v-model="formData.country"

          :required="true"
          :disabled="isRegistered"
      />
    </template>

    <template v-if="isForeignNational">
      <div class="guest-form__legal">
        <app-icon name="gavel" :size="19" class="guest-form__legal-icon"/>
        <p>{{ $t('checkinPage.legalNote') }}</p>
      </div>

      <div class="ww-grid-2">
        <label class="ww-field" :for="props.id + '-document-type'">
          <span class="ww-field__label">{{ $t("documentType") }}</span>
          <select
              class="ww-input"
              :id="props.id + '-document-type'"
              :aria-invalid="isEmpty(formData.documentType) ? undefined : !validate(formData.documentType)"
              v-model="formData.documentType"

              required
              :disabled="isRegistered"
          >
            <option value="" selected>{{ $t("select") }}</option>
            <option value="national_id">{{ $t("nationalId") }}</option>
            <option value="passport">{{ $t("passport") }}</option>
            <option value="driving_license">{{ $t("drivingLicense") }}</option>
          </select>
        </label>

        <label class="ww-field" :for="props.id + '-document-number'">
          <span class="ww-field__label">{{ $t("documentNumber") }}</span>
          <input
              type="text"
              class="ww-input"
              :id="props.id + '-document-number'"
              :placeholder="$t('documentNumber')"
              :aria-invalid="isEmpty(formData.documentNumber) ? undefined : !validate(formData.documentNumber)"
              v-model="formData.documentNumber"

              required
              :disabled="isRegistered"
          />
        </label>
      </div>

      <label class="ww-field" :for="props.id + '-purpose-of-stay'">
        <span class="ww-field__label">{{ $t("purposeOfStay") }}</span>
        <select
            class="ww-input"
            :id="props.id + '-purpose-of-stay'"
            :aria-invalid="isEmpty(formData.purposeOfStay) ? undefined : !validate(formData.purposeOfStay)"
            v-model="formData.purposeOfStay"

            required
            :disabled="isRegistered"
        >
          <option value="" selected>{{ $t("select") }}</option>
          <option value="tourism">{{ $t("tourism") }}</option>
          <option value="business">{{ $t("businessTrip") }}</option>
        </select>
      </label>
    </template>

    <p v-if="hasDraft" class="guest-form__draft">
      <app-icon name="check" :size="16"/>
      {{ $t('checkinPage.draftSaved') }}
    </p>

    <button
        v-if="!isRegistered"
        type="submit"
        class="ww-btn ww-btn--primary ww-btn--sm guest-form__submit"
        :disabled="isSaving"
    >
      <span v-if="isSaving" class="ww-spinner"></span>
      <app-icon v-else name="save" :size="19"/>
      {{ $t("save") }}
    </button>
    <button
        v-else
        type="button"
        class="ww-btn ww-btn--ghost ww-btn--sm guest-form__submit"
        @click="isRegistered = false"
    >
      <app-icon name="edit" :size="19"/>
      {{ $t("change") }}
    </button>
  </form>
</template>

<style scoped>
.guest-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.guest-form__zip-city {
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 12px;
}

.guest-form__submit {
  margin-top: 4px;
}

.guest-form__legal {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 12px 13px;
  background: var(--ww-warn-tint);
  border: 1px solid var(--ww-warn-border);
  border-radius: var(--ww-r-sm);
}

.guest-form__legal-icon {
  color: var(--ww-warn-icon);
}

.guest-form__legal p {
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.5;
  color: var(--ww-warn-ink);
}
</style>
